import React, { useContext, useEffect, useRef, useState } from 'react';
import { CameraContext } from '../../../contexts/CameraContext';
import classNames from 'classnames';

import * as PhotoStages from '../../../enums/photo-stage';
import * as FlowTypes from '../../../enums/flow-type';

import instructions_faceCamera from '../../../sounds/stand_facing_camera.mp3';
import instructions_turnLeft from '../../../sounds/turn_left_90deg.mp3';
import instructions_rightSideCamera from '../../../sounds/right_side_facing_camera.mp3';
import instructions_backSideCamera from '../../../sounds/back_side_facing_camera.mp3';
import instructions_leftSideCamera from '../../../sounds/left_side_facing_camera.mp3';

// ui stuff - scss, images
import { ReactComponent as NinetyDegrees } from '../../../styles/svg-components/90degrees.svg';
import { ReactComponent as BodyOutline } from '../../../styles/svg-components/human-body-outline-drawing-9.svg';
import { ReactComponent as Shutter } from '../../../styles/svg-components/shutter.svg';
import './FlowStageTakePhotos.scss';
import AutoProgressTimer from './AutoProgressTimer';
import BodyScanHeader from './BodyScanHeader';

import Div100Vh from 'react-div-100vh';

const instructionsForStage = (stage) => {
  switch (stage) {
    case PhotoStages.RIGHT:
      return [instructions_turnLeft, instructions_rightSideCamera];
    case PhotoStages.REAR:
      return [instructions_turnLeft, instructions_backSideCamera];
    case PhotoStages.LEFT:
      return [instructions_turnLeft, instructions_leftSideCamera];
    default:
      return [instructions_faceCamera];
  }
};

const FlowStageTakePhotos = ({ onPhotoCaptured, photos, onVerify, onCancel, onRestart }) => {
  const vRef = useRef(null);
  const bgVref = useRef(null);
  const [currentStage, setCurrentStage] = useState(PhotoStages.FRONT);
  const { setIsCapturing, setVideoRef, cameraUnavaiable, photoFromStream, isCapturing, stream } = useContext(
    CameraContext
  );
  const [showProgress, setShowProgress] = useState(false);
  const [bodyImageDimensions, setBodyImageDimensions] = useState({});
  const [audio, setAudio] = useState(null);

  useEffect(() => {
    if (!!audio) {
      audio.play();
    }
  }, [audio]);

  useEffect(() => {
    setTimeout(async () => {
      if (!!vRef) {
        vRef.current.onloadeddata = () => {
          setBodyImageDimensions({ height: `${vRef.current.clientHeight * 0.95}px` });
        };
        setVideoRef(vRef);
      }
    }, 200);
    return function cleanup() {
      console.log('cleanup setIsCapturing false');
      setIsCapturing(null);
    };
  }, [vRef]);

  useEffect(() => {
    console.log('useEffect bgVref, stream', bgVref, stream)
    setTimeout(() => {
      if (!!bgVref && !!stream) {
        bgVref.current.srcObject = stream;
      }
    })
  }, [bgVref, stream]);

  useEffect(() => {
    console.log('FlowStageTakePhotos photos effect', photos);
    if (photos.length >= 4) {
      setIsCapturing(null);
    }
  }, [photos]);

  const getImageFromStream = () => {
    var dataUrl = photoFromStream();
    onPhotoCaptured(dataUrl);
  };

  const playSounds = (stage) => {
    const sounds = instructionsForStage(stage);
    const firstAudio = new Audio(sounds[0]);
    const secondAudio = sounds.length > 1 ? new Audio(sounds[1]) : null;

    const ended = () => {
      if (!!secondAudio) {
        setAudio(secondAudio);
      } else {
        setAudio(null);
      }
    };
    firstAudio.addEventListener('ended', ended);
    setAudio(firstAudio);
  };

  const takePhoto = () => {
    setShowProgress(false);
    getImageFromStream();
    const next = PhotoStages.next(currentStage);
    setCurrentStage(PhotoStages.next(currentStage));
    if (next !== PhotoStages.DONE && isCapturing === FlowTypes.SELFIE) {
      playSounds(next);
      setTimeout(() => {
        setShowProgress(true);
      }, 5000);
    }
  };

  const startAutoProgress = () => {
    setTimeout(() => {
      playSounds(currentStage);
      setShowProgress(true);
    });
  };

  const showThumbnails = () => !!photos && !!photos.length;

  const showBottomUI = () => showThumbnails() || isCapturing === FlowTypes.ASSISTED;

  const showShutter = () => isCapturing === FlowTypes.ASSISTED && (!photos || photos.length < 4);

  const showSelfieStart = () => isCapturing === FlowTypes.SELFIE && currentStage === PhotoStages.FRONT && !showProgress;

  return (
    <Div100Vh className="flow-stage-take-photo--container">
      <BodyScanHeader {...{ onRestart, onCancel }} />
      {(cameraUnavaiable === null || !!cameraUnavaiable) && (
        <p>It looks like you have no camera or have denied its usage</p>
      )}
      {cameraUnavaiable !== null && !cameraUnavaiable && (
        <div className="video-display">
          {currentStage !== PhotoStages.DONE && (
            <>
              {!!stream && (
                <div className="blurred-bg">
                  <video ref={bgVref} autoPlay playsInline muted></video>
                </div>
              )}
              <video
                className={classNames({ selfie: isCapturing === FlowTypes.SELFIE })}
                ref={vRef}
                autoPlay
                playsInline
                muted
                id="flow-stage-take-photo--video-el"
              ></video>
              {currentStage !== PhotoStages.FRONT && <NinetyDegrees className="rotate-img" />}
              {!!bodyImageDimensions && bodyImageDimensions.height && (
                <BodyOutline className="body-outline" style={bodyImageDimensions} />
              )}
              <h1>{currentStage + 1}</h1>
              {showSelfieStart() && <button onClick={startAutoProgress}>START!</button>}
            </>
          )}

          {currentStage === PhotoStages.DONE && (
            <div className="done-instructions">
              <p>Finished! On the next screen please check your photos to make sure your whole body is on screen.</p>
              <button onClick={onVerify}>Next</button>
            </div>
          )}

          {!!showProgress && <AutoProgressTimer onCountdownFinished={takePhoto}></AutoProgressTimer>}
          {!!showBottomUI() && (
            <div
              className={classNames({
                bottomUiBar: true,
                isAssisted: isCapturing === FlowTypes.ASSISTED,
                showShutter: showShutter(),
              })}
            >
              {!!showThumbnails() && (
                <div className="thumbnails">
                  {photos.map((m, i) => (
                    <img src={m} key={i} alt={`scan ${i}`} />
                  ))}
                </div>
              )}
              {showShutter() && <Shutter role="button" onClick={takePhoto} className="shutter" />}
            </div>
          )}
        </div>
      )}
    </Div100Vh>
  );
};

export default FlowStageTakePhotos;
